import {AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {SourceElementUtils} from '@application/helper/source-element-utils';
import {Coordinates} from '@domain/coordinates';
import {PositionOfDialog} from '@domain/position-of-dialog';
import {BmpProductionScheduleItemInPathGroup} from '@domain/production-schedule/bmp-production-schedule-item-in-path-group';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {EpProductionScheduleItemInPathGroup} from '@domain/production-schedule/ep-production-schedule-item-in-path-group';
import {FinishingForProductionSchedule} from '@domain/production-schedule/finishing-for-production-schedule';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionScheduleItemInPathGroupStatus} from '@domain/production-schedule/production-schedule-item-in-path-group-status';
import {ProductionScheduleOrderLine} from '@domain/production-schedule/production-schedule-order-line';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription as ProfileSubscription} from '@domain/profile/subscription';
import {Authentication, AUTHENTICATION} from '@infrastructure/http/authentication/authentication';
import {RepositionDialogComponent} from '@presentation/components/reposition-dialog/reposition-dialog.component';
import {SelectFinishingComponent} from '@presentation/components/select-finishing/select-finishing.component';
import {FinishingDetailsComponent} from '@presentation/pages/texfab/production-schedule/finishing-details/finishing-details.component';
import {
  AssertionUtils,
  BaseComponent,
  canShowErrorForErrorCodeAndFormControlForFormGroup,
  convertCommercialUnit,
  convertHeightInMeterToPicks,
  DialogBuilderFactoryService,
  TranslateService,
  Unit,
  WINDOW
} from '@vdw/angular-component-library';
import {find, isEmpty, isEqual, isNil, isUndefined, max} from 'lodash-es';
import {filter, takeUntil} from 'rxjs/operators';
import {EditProductionScheduleItemInPathGroupAction} from './edit-production-schedule-item-in-path-group-action';

@Component({
  templateUrl: './edit-production-schedule-item-in-path-group.component.html',
  styleUrls: ['./edit-production-schedule-item-in-path-group.component.scss']
})
export class EditProductionScheduleItemInPathGroupComponent extends BaseComponent implements OnInit, AfterViewInit {
  @ViewChild('matCardElement', {read: ElementRef}) public matCardElement: ElementRef;

  public productionScheduleItemInPathGroupForm: FormGroup<{
    name: FormControl<string>;
    code: FormControl<string>;
    quantity: FormControl<number>;
    amountProduced: FormControl<number>;
    commercialHeight?: FormControl<number>;
    commercialWidth?: FormControl<number>;
    technicalHeight?: FormControl<number>;
    technicalWidth?: FormControl<number>;
    cutbarBeforeCommercialLength?: FormControl<number>;
    cutbarAfterCommercialLength?: FormControl<number>;
    finishing?: FormControl<FinishingForProductionSchedule>;
  }>;

  public arrowPosition: Coordinates;
  public readonly commercialUnit: Unit;
  public quantityLeft: number;
  public orderLine: ProductionScheduleOrderLine;
  public completingPartially = false;
  public readonly productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup;
  public readonly FINISHING_CHIP_VALUE_KEY = '%(nameWithVersion)';
  public readonly FINISHING_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.machineQuality.editFinishing.absolutePath;

  private readonly matDialogPosition: Coordinates;
  private readonly productionSchedule: ProductionSchedule;
  private readonly cursorOffset = 4;
  private readonly matIconWidth = 24;
  private readonly allowExceedingRequestedAmountOfOrderLineItems: boolean;
  private readonly changeFinishingPermission: Permission = Permission.CHANGE_INDIVIDUAL_FINISHING;
  private readonly minimumVerticalMarginInPx = 16;
  private currentSubscription: ProfileSubscription;
  private pickDensityInPicksPerMeter: number;

  public constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    @Inject(WINDOW) private readonly window: Window,
    private readonly formBuilder: FormBuilder,
    private readonly dialogRef: MatDialogRef<EditProductionScheduleItemInPathGroupComponent>,
    private readonly translate: TranslateService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService
  ) {
    super();

    this.pickDensityInPicksPerMeter = data.pickDensityInPicksPerMeter;
    this.productionScheduleItemInPathGroup = data.productionScheduleItemInPathGroup;
    this.commercialUnit = data.commercialUnit;
    this.productionSchedule = data.productionSchedule;
    this.matDialogPosition = data.matDialogPosition;
    this.completingPartially = this.productionSchedule.completingPartially;
    this.allowExceedingRequestedAmountOfOrderLineItems = data.allowExceedingRequestedAmountOfOrderLineItems;
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
    if (this.productionScheduleItemInPathGroup.hasOrderLine()) {
      this.getOrderLineAndCalculateQuantityLeft();
    }

    this.setFormFields();
    this.setDrawingTypeSpecificFormFields();
  }

  public ngAfterViewInit(): void {
    this.repositionMatDialog();
  }

  public canShowCode(): boolean {
    return !isEmpty(this.productionScheduleItemInPathGroup.drawing.code);
  }

  public canShowCommercialDimensions(): boolean {
    return !isNil(this.productionScheduleItemInPathGroup.commercialDimensionsInMM);
  }

  public canShowTechnicalDimensions(): boolean {
    return !isNil(this.productionScheduleItemInPathGroup.technicalDimensions);
  }

  public canShowFinishingField(): boolean {
    return (
      this.currentSubscription?.hasPermission(RouteUtils.paths.texStyle.finishingTemplate.editFinishingTemplate.requiredPermission) &&
      this.productionScheduleItemInPathGroup.drawing.drawingType === DrawingType.BMP &&
      (!this.hasOrderLine() || (this.hasOrderLine() && this.hasChosenFinishing()))
    );
  }

  public canShowCutbarsFields(): boolean {
    return isEqual(this.productionScheduleItemInPathGroup.drawing.drawingType, DrawingType.EP);
  }

  public getMaximumValueForQuantity(): number {
    return this.orderLine.amountLeft;
  }

  public getAmountOfCarpetsBasedOnQuantity(): number {
    if (!this.productionScheduleItemInPathGroupForm.controls.quantity.hasError('max')) {
      return Math.floor(+this.productionScheduleItemInPathGroupForm.getRawValue().quantity / this.productionSchedule.getProductionScheduleItemMultiplier());
    }
    return Math.floor(this.getMaximumValueForQuantity() / this.productionSchedule.getProductionScheduleItemMultiplier());
  }

  public hasChosenFinishing(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.productionScheduleItemInPathGroupForm.getRawValue().finishing);
  }

  public getCurrentFinishing(): FinishingForProductionSchedule {
    return this.productionScheduleItemInPathGroupForm.getRawValue().finishing;
  }

  public changeCurrentFinishing(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openSelectGridDialog(SelectFinishingComponent, {
        machineQualityId: this.productionSchedule.machineQuality.id
      })
      .pipe(
        filter((finishing: FinishingForProductionSchedule) => !!finishing),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe((finishing: FinishingForProductionSchedule) => this.productionScheduleItemInPathGroupForm.controls.finishing.patchValue(finishing));
  }

  public deleteFinishing(): void {
    if (isEqual(this.productionScheduleItemInPathGroupForm.getRawValue().finishing, this.productionSchedule.finishing)) {
      this.productionScheduleItemInPathGroupForm.controls.finishing.patchValue(null);
    } else {
      this.productionScheduleItemInPathGroupForm.controls.finishing.patchValue(this.productionSchedule.finishing);
    }
  }

  public cancelEvent(): void {
    this.completingPartially ? this.deleteProductionScheduleItemInPathGroupInCompletingPartiallyProductionOrder() : this.deleteProductionScheduleItemInPathGroup();
  }

  public applyChangesToProductionScheduleItemInPathGroup(): void {
    if (this.canChangeCommercialDimensions()) {
      this.productionScheduleItemInPathGroup.commercialDimensionsInMM.heightInMM = convertCommercialUnit({
        from: {
          unit: this.commercialUnit,
          value: this.productionScheduleItemInPathGroupForm.getRawValue().commercialHeight
        },
        to: Unit.MILLIMETER
      });

      this.productionScheduleItemInPathGroup.technicalDimensions.heightInPicks = this.productionScheduleItemInPathGroupForm.getRawValue().technicalHeight;
    }

    this.productionScheduleItemInPathGroup.adjustQuantityOfDrawingsInPathGroup(
      Math.floor(+this.productionScheduleItemInPathGroupForm.getRawValue().quantity / this.productionSchedule.getProductionScheduleItemMultiplier())
    );
    this.productionScheduleItemInPathGroup.amountProduced = this.productionScheduleItemInPathGroupForm.getRawValue().amountProduced;

    if (this.completingPartially && isEqual(this.productionScheduleItemInPathGroup.amountProduced, 0)) {
      this.productionScheduleItemInPathGroup.status = ProductionScheduleItemInPathGroupStatus.REMOVED;
    }

    switch (this.productionScheduleItemInPathGroup.drawing.drawingType) {
      case DrawingType.EP:
        this.applySettingsToEpProductionScheduleItemInPathGroup();
        break;

      case DrawingType.BMP:
        this.applySettingsToBmpProductionScheduleItemInPathGroup();
    }

    this.dialogRef.close({action: EditProductionScheduleItemInPathGroupAction.EDIT, value: this.productionScheduleItemInPathGroup});
  }

  public canEditProductionScheduleItemInPathGroup(): boolean {
    return this.productionScheduleItemInPathGroupForm.valid;
  }

  public canShowArrow(): boolean {
    return !isUndefined(this.arrowPosition);
  }

  public showFinishingDetails(event: MouseEvent): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .withClass('reposition-dialog')
      .withWidth('312px')
      .withMaxHeight('100%')
      .openDialog(RepositionDialogComponent, {
        finishing: this.getCurrentFinishing(),
        component: FinishingDetailsComponent,
        sourceElement: SourceElementUtils.findSourceElementForDialog(event, 'info-balloon-container'),
        positionOfDialog: PositionOfDialog.LEFT
      });
  }

  public canShowErrorForFormControl(formControlName: string, errorCode: string): boolean {
    return canShowErrorForErrorCodeAndFormControlForFormGroup(errorCode, formControlName, this.productionScheduleItemInPathGroupForm);
  }

  public hasOrderLine(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.orderLine);
  }

  public hasChangeFinishingPermission(): boolean {
    return this.currentSubscription?.hasPermission(this.changeFinishingPermission);
  }

  public disableEditFinishing(): boolean {
    return !this.hasChangeFinishingPermission() || this.completingPartially;
  }

  public noPermissionsToSelectFinishingHint(): string {
    return this.hasChangeFinishingPermission() ? '' : `(${this.translate.instant('GENERAL.HINTS.UNAUTHORIZED')})`;
  }

  private canChangeCommercialDimensions(): boolean {
    return this.productionScheduleItemInPathGroup.drawing.repeated;
  }

  private setFormFields(): void {
    const validatorsForAmountProducedFormControl = [Validators.required];

    this.productionScheduleItemInPathGroupForm = this.formBuilder.group({
      name: this.formBuilder.control({value: this.productionScheduleItemInPathGroup.drawing.name, disabled: true}),
      code: this.formBuilder.control({value: this.productionScheduleItemInPathGroup.drawing.code, disabled: true}),
      quantity: this.formBuilder.control(
        {value: this.productionScheduleItemInPathGroup.quantity * this.productionSchedule.getProductionScheduleItemMultiplier(), disabled: false},
        !this.allowExceedingRequestedAmountOfOrderLineItems && this.hasOrderLine()
          ? [Validators.min(1), Validators.required, Validators.max(this.getMaximumValueForQuantity())]
          : [Validators.min(1), Validators.required]
      ),
      amountProduced: this.formBuilder.control(this.productionScheduleItemInPathGroup.amountProduced, this.completingPartially ? validatorsForAmountProducedFormControl : null)
    });

    if (this.hasOrderLine()) {
      this.subscribeToQuantityValueChanges();
    }

    if (this.canShowCommercialDimensions()) {
      this.productionScheduleItemInPathGroupForm.addControl(
        'commercialHeight',
        this.formBuilder.control(
          {
            value: convertCommercialUnit({
              from: {
                unit: Unit.MILLIMETER,
                value: this.productionScheduleItemInPathGroup.commercialDimensionsInMM.heightInMM
              },
              to: this.commercialUnit
            }),
            disabled: !this.canChangeCommercialDimensions()
          },
          [Validators.required, Validators.min(1)]
        )
      );
      this.productionScheduleItemInPathGroupForm.addControl(
        'commercialWidth',
        this.formBuilder.control({
          value: convertCommercialUnit({
            from: {
              unit: Unit.MILLIMETER,
              value: this.productionScheduleItemInPathGroup.commercialDimensionsInMM.widthInMM
            },
            to: this.commercialUnit
          }),
          disabled: true
        })
      );
    }

    if (this.canShowTechnicalDimensions()) {
      this.productionScheduleItemInPathGroupForm.addControl(
        'technicalHeight',
        this.formBuilder.control({value: this.productionScheduleItemInPathGroup.technicalDimensions.heightInPicks, disabled: true})
      );
      this.productionScheduleItemInPathGroupForm.addControl(
        'technicalWidth',
        this.formBuilder.control({value: this.productionScheduleItemInPathGroup.technicalDimensions.widthInDents, disabled: true})
      );
    }

    if (this.canShowCommercialDimensions() && this.canShowTechnicalDimensions() && this.canChangeCommercialDimensions()) {
      this.subscribeToCommercialHeightValueChanges();
    }
  }

  private setDrawingTypeSpecificFormFields(): void {
    switch (this.productionScheduleItemInPathGroup.drawing.drawingType) {
      case DrawingType.EP:
        const epProductionScheduleItemInPathGroup: EpProductionScheduleItemInPathGroup = this.productionScheduleItemInPathGroup as any as EpProductionScheduleItemInPathGroup;
        this.productionScheduleItemInPathGroupForm.addControl(
          'cutbarBeforeCommercialLength',
          this.formBuilder.control(epProductionScheduleItemInPathGroup.cutbarsLength.cutbarBefore.commercialLengthInMM)
        );
        this.productionScheduleItemInPathGroupForm.addControl(
          'cutbarAfterCommercialLength',
          this.formBuilder.control(epProductionScheduleItemInPathGroup.cutbarsLength.cutbarAfter.commercialLengthInMM)
        );
        break;

      case DrawingType.BMP:
        const bmpProductionScheduleItemInPathGroup: BmpProductionScheduleItemInPathGroup = this.productionScheduleItemInPathGroup as any as BmpProductionScheduleItemInPathGroup;
        this.productionScheduleItemInPathGroupForm.addControl(
          'finishing',
          this.formBuilder.control({value: bmpProductionScheduleItemInPathGroup.finishing, disabled: this.hasOrderLine() || this.disableEditFinishing()})
        );
        break;
    }
  }

  private applySettingsToBmpProductionScheduleItemInPathGroup(): void {
    const bmpProductionScheduleItemInPathGroup: BmpProductionScheduleItemInPathGroup = this.productionScheduleItemInPathGroup as any as BmpProductionScheduleItemInPathGroup;
    bmpProductionScheduleItemInPathGroup.changeFinishing(this.productionScheduleItemInPathGroupForm.getRawValue().finishing);
  }

  private applySettingsToEpProductionScheduleItemInPathGroup(): void {
    const epProductionScheduleItemInPathGroup: EpProductionScheduleItemInPathGroup = this.productionScheduleItemInPathGroup as any as EpProductionScheduleItemInPathGroup;
    const cutbarBeforeCommercialLengthInMillimeters: number = convertCommercialUnit({
      from: {
        unit: this.commercialUnit,
        value: this.productionScheduleItemInPathGroupForm.getRawValue().cutbarBeforeCommercialLength
      },
      to: Unit.MILLIMETER
    });
    epProductionScheduleItemInPathGroup.cutbarsLength.cutbarBefore.commercialLengthInMM = cutbarBeforeCommercialLengthInMillimeters;
    epProductionScheduleItemInPathGroup.cutbarsLength.cutbarBefore.technicalLengthInNumberOfPicks = convertHeightInMeterToPicks(
      convertCommercialUnit({
        from: {
          value: cutbarBeforeCommercialLengthInMillimeters,
          unit: Unit.MILLIMETER
        },
        to: Unit.METER
      }),
      this.pickDensityInPicksPerMeter,
      this.productionSchedule?.machineQuality?.weaveStructure
    );

    const cutbarAfterCommercialLengthInMillimeters: number = convertCommercialUnit({
      from: {
        unit: this.commercialUnit,
        value: this.productionScheduleItemInPathGroupForm.getRawValue().cutbarAfterCommercialLength
      },
      to: Unit.MILLIMETER
    });
    epProductionScheduleItemInPathGroup.cutbarsLength.cutbarAfter.commercialLengthInMM = cutbarAfterCommercialLengthInMillimeters;
    epProductionScheduleItemInPathGroup.cutbarsLength.cutbarAfter.technicalLengthInNumberOfPicks = convertHeightInMeterToPicks(
      convertCommercialUnit({
        from: {
          unit: Unit.MILLIMETER,
          value: cutbarAfterCommercialLengthInMillimeters
        },
        to: Unit.METER
      }),
      this.pickDensityInPicksPerMeter,
      this.productionSchedule?.machineQuality?.weaveStructure
    );
  }

  private repositionMatDialog(): void {
    if (!isUndefined(this.matDialogPosition)) {
      const matCardHeight = this.matCardElement.nativeElement.offsetHeight;
      const matCardWidth = this.matCardElement.nativeElement.offsetWidth;

      let matDialogPositionTop = max([this.matDialogPosition.y - matCardHeight / 2, this.minimumVerticalMarginInPx]);

      if (matDialogPositionTop + matCardHeight >= this.window.innerHeight) {
        matDialogPositionTop = this.window.innerHeight - matCardHeight - this.minimumVerticalMarginInPx;
      }

      this.dialogRef.updatePosition({
        top: `${matDialogPositionTop}px`,
        left: `${this.matDialogPosition.x - matCardWidth - this.matIconWidth / 2}px`
      });

      setTimeout(() => {
        this.setArrowPositions();
      });
    }
  }

  private setArrowPositions(): void {
    const matIconHeight = 24;
    const matIconOffset = 4;

    this.arrowPosition = {
      y: this.matDialogPosition.y - matIconHeight / 2,
      x: this.matDialogPosition.x - this.matIconWidth + matIconOffset + this.cursorOffset
    };
  }

  private getOrderLineAndCalculateQuantityLeft(): void {
    this.orderLine = find(this.productionSchedule.orderLines, {id: this.productionScheduleItemInPathGroup.orderLineId});

    if (!AssertionUtils.isNullOrUndefined(this.orderLine)) {
      this.quantityLeft = max([this.orderLine.getAmountLeftForProductionSchedule(this.productionSchedule.getProductionScheduleItemMultiplier()), 0]);
    }
  }

  private subscribeToQuantityValueChanges(): void {
    this.productionScheduleItemInPathGroupForm.controls.quantity.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((newQuantity: number) => {
      this.quantityLeft = max([
        this.orderLine.getAmountLeftForProductionSchedule(this.productionSchedule.getProductionScheduleItemMultiplier()) +
          (this.productionScheduleItemInPathGroup.quantity - newQuantity) * this.productionSchedule.getProductionScheduleItemMultiplier(),
        0
      ]);
    });
  }

  private subscribeToCommercialHeightValueChanges(): void {
    this.productionScheduleItemInPathGroupForm.controls.commercialHeight.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((commercialHeight: number) => {
      let heightInPicks = convertHeightInMeterToPicks(
        convertCommercialUnit({
          from: {
            unit: this.commercialUnit,
            value: commercialHeight
          },
          to: Unit.METER
        }),
        this.pickDensityInPicksPerMeter,
        this.productionSchedule?.machineQuality?.weaveStructure
      );

      while (!isEqual(heightInPicks % this.productionSchedule.machineQuality.weaveStructure.sendRepeat, 0)) {
        heightInPicks++;
      }

      this.productionScheduleItemInPathGroupForm.controls.technicalHeight.setValue(heightInPicks);
    });
  }

  private deleteProductionScheduleItemInPathGroup(): void {
    this.dialogRef.close({action: EditProductionScheduleItemInPathGroupAction.DELETE});
  }

  private deleteProductionScheduleItemInPathGroupInCompletingPartiallyProductionOrder(): void {
    this.dialogRef.close({action: EditProductionScheduleItemInPathGroupAction.DELETE_COMPLETING_PARTIALLY, value: this.productionScheduleItemInPathGroup});
  }
}
